<template>
    <app-tooltip :value="tooltip" :disabled="!tooltip">
        <v-autocomplete
            ref="input"
            :key="autocompleteKey"
            v-model="selected"
            class="app-query-selector"
            :items="items"
            :loading="setupLoading || searchLoading"
            :disabled="setupLoading || disabled"
            :clearable="!required"
            hide-details
            :label="label"
            return-object
            :item-text="itemText"
            :item-value="itemKey"
            color="primaryText"
            item-color="primaryText"
            outlined
            dense
            cache-items
            :search-input.sync="searchCriteria"
            @input="v => onItemSelected(v)"
            @clear="v => onItemSelected()"
        >
            <template #item="{ item }">
                <div v-intersect="onIntersect(item)" class="app-query-selector__item">
                    <app-tooltip :value="getItemTooltip(item)" :disabled="!getItemTooltip(item)">
                        {{ getItemText(item) }}
                    </app-tooltip>
                </div>
            </template>
        </v-autocomplete>
    </app-tooltip>
</template>

<script>
import { Collection } from '@/models';
import snack from '@/lib/helpers/snack';

export default {
    name: 'AppQuerySelector',
    props: {
        collection: {
            type: Collection,
            default: null
        },
        required: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        queryKey: {
            type: String,
            default: 'id'
        },
        itemKey: {
            type: String,
            default: 'id'
        },
        itemText: {
            type: [String, Function],
            default: 'name'
        },
        itemTooltip: {
            type: [String, Function],
            default: 'name'
        },
        label: {
            type: String,
            default: null
        },
        loadObject: {
            type: Boolean,
            default: false
        },
        presetItems: {
            type: Array,
            default: null
        },
        filters: {
            type: Object,
            default: () => ({})
        },
        tooltip: {
            type: [String, Array],
            default: null
        }
    },
    data: () => ({
        items: [],
        initialItems: [],
        selected: null,
        searchCriteria: null,
        searchLoading: false,
        searchDebounced: () => {},
        searchRequests: [],
        total: null,
        offset: 0,
        limit: 40,
        loadingMore: false,
        autocompleteKey: 0
    }),
    watch: {
        searchCriteria(criteria) {
            if (this.presetItems) {
                return;
            }

            if (!criteria || criteria.length < 2) {
                this.items = this.$clone(this.initialItems);
                return;
            }

            this.searchDebounced(criteria);
        },
        filters(v = null, pv = null) {
            if (JSON.stringify(v) !== JSON.stringify(pv)) {
                this.autocompleteKey++;
                this.setup();
            }
        }
    },
    methods: {
        clear() {
            this.selected = null;
            this.searchRequests = [];
            this.items = [];
            this.initialItems = [];
            this.onItemSelected();

            this.autocompleteKey++;

            this.setup();
        },
        async setup() {
            this.searchDebounced = this._.debounce(this.search, 500);

            try {
                let queryValue = this.getQueryParam(this.queryKey);

                if (!queryValue) {
                    queryValue = this.$store.getters['app/query_selector'](this.queryKey);
                }

                if (this.presetItems) {
                    this.items = this.presetItems;
                } else {
                    this.items = [];

                    const response = await this.collection.request(null, this.limit, [], this.filters);
                    if (response && 'data' in response && Array.isArray(response.data)) {
                        this.items = response.data;
                        this.offset = this.items.length;
                        this.total = response.metadata.total;
                    }
                }

                if (queryValue) {
                    const existing = this.items.find(item => item[`${this.itemKey}`] === queryValue);
                    if (!existing) {
                        const found = await this.collection.item(queryValue);
                        if (found) {
                            this.items.unshift(found);
                        }
                    }

                    this.selected = queryValue;
                    this.$store.dispatch('app/addQuerySelector', { key: this.queryKey, value: queryValue });
                    this.replaceQueryParam(this.queryKey, queryValue);
                } else if (this.required) {
                    this.selected = this.items[0][`${this.itemKey}`];
                    this.onItemSelected(this.items[0]);
                }

                this.initialItems = this.$clone(this.items);

                if (this.loadObject) {
                    this.$emit(
                        'load',
                        this.items.find(item => item[this.itemKey] === this.selected)
                    );
                } else {
                    this.$emit('load', this.selected);
                }
            } catch (error) {
                if (error?.code === 404) {
                    if (error.response?.data?.message) {
                        snack({
                            timeout: 20000,
                            type: 'error',
                            text: error.response.data.message
                        });
                    }
                } else {
                    this.handleError(error, () => this.setup());
                }
            }
        },
        onItemSelected(item) {
            if (this.required && !item) {
                return;
            }

            const value = item ? item[this.itemKey] : null;

            if (this.getQueryParam(this.queryKey) !== value) {
                this.replaceQueryParam(this.queryKey, value);
                this.$store.dispatch('app/addQuerySelector', { key: this.queryKey, value });
                if (this.loadObject) {
                    this.$emit('change', item);
                } else {
                    this.$emit('change', value);
                }
            }
        },
        async search(criteria) {
            this.searchLoading = true;

            try {
                this.searchRequests.push(criteria);

                const results = await this.collection.search(criteria, this.filters);

                if (this.searchRequests[this.searchRequests.length - 1] === criteria) {
                    this.items = results;
                }
            } catch (error) {
                this.handleError(error, () => this.search(criteria));
            }

            this.searchLoading = false;
        },
        onIntersect(item) {
            const index = this.items.indexOf(item);

            if (this.total > this.limit && index >= this.items.length - 3) {
                this.loadMore();
            }
        },
        async loadMore() {
            if (this.presetItems) {
                return;
            }

            if (this.loadingMore) {
                return;
            }

            if (this.total !== null && this.offset >= this.total) {
                return;
            }

            this.loadingMore = true;

            try {
                const response = await this.collection.request(this.offset, this.limit, [], this.filters);
                if (response && 'data' in response && Array.isArray(response.data)) {
                    this.items.push(...response.data);
                    this.offset = this.items.length;
                    this.total = response.metadata.total;
                }
            } catch (error) {
                this.handleError(error, this.loadMore);
            }

            this.loadingMore = false;
        },
        getItemText(item) {
            if (typeof this.itemText === 'function') {
                return this.itemText(item);
            }

            return item[`${this.itemText}`];
        },
        getItemTooltip(item) {
            if (!this.itemTooltip) {
                return null;
            }

            if (typeof this.itemTooltip === 'function') {
                try {
                    return this.itemTooltip(item);
                } catch (error) {
                    console.error(error);
                    return null;
                }
            }

            return item[`${this.itemTooltip}`];
        }
    }
};
</script>

<style lang="scss" scoped>
.app-query-selector {
    min-width: 12rem;
    &__item {
        color: var(--v-primaryText-base);
        font-size: 0.8125rem;
    }
}
</style>
